// apps/web/src/features/cargo/components/CargoAddModal.tsx
//
// Manual "add cargo" modal (#1232) for admin / logist_ru / logist_cn. React
// Hook Form + zodResolver over the form-layer schema; numeric-like fields
// validate (comma OR dot) and submit normalized source-compatible strings via
// `toCreateInput`. The status selector offers ONLY the two stored statuses.
// EditorModal supplies the unsaved-changes guard from `formState.isDirty`.

import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';

import { Button, EditorModal, useEditorGuard } from '../../../ui/index.js';
import { useToastStore } from '../../../stores/toast.js';
import { errorMessage } from '../../../api/errors.js';
import { useCreateCargo } from '../mutations.js';
import {
  CargoAddFormSchema,
  emptyCargoAddValues,
  toCreateInput,
  type CargoAddFormValues,
} from '../forms/schemas.js';
import {
  cargoStatusOptions,
  useCargoDictionaryOptions,
  type CargoManagerOption,
} from '../forms/useCargoFormOptions.js';
import { CargoDateField, CargoSelectField, CargoTextField } from '../forms/fields.js';

export interface CargoAddModalProps {
  managerOptions: readonly CargoManagerOption[];
  onClose: () => void;
}

export function CargoAddModal({ managerOptions, onClose }: CargoAddModalProps) {
  const { t } = useTranslation(['cargo', 'common']);
  const create = useCreateCargo();
  const showToast = useToastStore((s) => s.show);
  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    reset,
  } = useForm<CargoAddFormValues>({
    resolver: zodResolver(CargoAddFormSchema),
    defaultValues: emptyCargoAddValues(),
  });

  const dict = useCargoDictionaryOptions(true, '', '');

  const numericError = (key: keyof CargoAddFormValues): string | undefined =>
    errors[key] ? t('cargo:forms.errors.numeric') : undefined;

  const onSubmit = (values: CargoAddFormValues) => {
    create.mutate(toCreateInput(values), {
      onSuccess: () => {
        reset(emptyCargoAddValues());
        showToast({ tone: 'success', text: t('cargo:forms.toasts.created') });
        onClose();
      },
      onError: (err) => showToast({ tone: 'error', text: errorMessage(err) }),
    });
  };

  const managerSelectOptions = managerOptions.map((m) => ({ value: m.id, label: m.name }));
  const unset = { value: '', label: t('cargo:forms.unset') };

  return (
    <EditorModal
      open
      title={t('cargo:forms.addTitle')}
      width={560}
      dirty={isDirty}
      busy={create.isPending}
      onConfirmClose={onClose}
    >
      <form onSubmit={handleSubmit(onSubmit)} noValidate data-testid="cargo-add-form">
        <CargoTextField
          control={control}
          name="article"
          label={t('cargo:forms.fields.article')}
          error={errors.article ? t('cargo:forms.errors.required') : undefined}
        />
        <CargoSelectField
          control={control}
          name="responsibleManagerId"
          label={t('cargo:forms.fields.manager')}
          options={managerSelectOptions}
          placeholderOption={unset}
          testId="cargo-add-manager"
        />
        <CargoSelectField
          control={control}
          name="carrier"
          label={t('cargo:forms.fields.carrier')}
          options={dict.carrierOptions}
          placeholderOption={unset}
          testId="cargo-add-carrier"
        />
        <CargoSelectField
          control={control}
          name="pathFormat"
          label={t('cargo:forms.fields.pathFormat')}
          options={dict.pathFormatOptions}
          placeholderOption={unset}
          testId="cargo-add-path-format"
        />
        <CargoSelectField
          control={control}
          name="status"
          label={t('cargo:forms.fields.status')}
          options={cargoStatusOptions()}
          testId="cargo-add-status"
        />
        <CargoTextField
          control={control}
          name="nameGoods"
          label={t('cargo:forms.fields.nameGoods')}
        />
        <CargoTextField
          control={control}
          name="numberOfSeats"
          label={t('cargo:forms.fields.numberOfSeats')}
          error={numericError('numberOfSeats')}
        />
        <CargoTextField
          control={control}
          name="weight"
          label={t('cargo:forms.fields.weight')}
          error={numericError('weight')}
        />
        <CargoTextField
          control={control}
          name="volume"
          label={t('cargo:forms.fields.volume')}
          error={numericError('volume')}
        />
        <CargoTextField
          control={control}
          name="transportationTariff"
          label={t('cargo:forms.fields.transportationTariff')}
        />
        <CargoTextField
          control={control}
          name="costGoods"
          label={t('cargo:forms.fields.costGoods')}
          error={numericError('costGoods')}
        />
        <CargoTextField
          control={control}
          name="insuranceCost"
          label={t('cargo:forms.fields.insuranceCost')}
          error={numericError('insuranceCost')}
        />
        <CargoTextField
          control={control}
          name="packagingCost"
          label={t('cargo:forms.fields.packagingCost')}
          error={numericError('packagingCost')}
        />
        <CargoDateField
          control={control}
          name="timeFromChina"
          label={t('cargo:forms.fields.timeFromChina')}
        />
        <CargoTextField
          control={control}
          name="totalCost"
          label={t('cargo:forms.fields.totalCost')}
          error={numericError('totalCost')}
        />
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
          <CancelButton onClose={onClose} label={t('common:actions.cancel')} />
          <Button type="submit" kind="primary" disabled={create.isPending}>
            {t('common:actions.create')}
          </Button>
        </div>
      </form>
    </EditorModal>
  );
}

function CancelButton({ onClose, label }: { onClose: () => void; label: string }) {
  const { confirmDestructive } = useEditorGuard();
  return (
    <Button kind="ghost" onClick={() => confirmDestructive(onClose)}>
      {label}
    </Button>
  );
}
